import { AccountingPartner, AppSetting, Customer, Service } from './types';
import { CustomerState } from '@/Enums';
import { isPlainObject } from '@/scripts/Common/HelperFunctions';
import { isPerson, isAddress, isBankAccount, isBankTransferDetails, isLogo } from '@/scripts/Common/typeGuards';
import { BankAccount, Logo } from '@/scripts/Common/types';

// guards for the interfaces
// following proper safe guard https://dev.to/noriste/keeping-typescript-type-guards-safe-and-up-to-date-a-simpler-solution-ja3

export function isAppSetting(value: unknown): value is AppSetting {
  if (!isPlainObject(value)) return false;

  // Required fields
  if (!value.hasOwnProperty('apiKey')) return false;
  if (!value.hasOwnProperty('apiSecret')) return false;
  if (!value.hasOwnProperty('deviceId')) return false;
  if (!value.hasOwnProperty('deviceLabel')) return false;
  if (!value.hasOwnProperty('deviceName')) return false;
  if (!value.hasOwnProperty('serverUri')) return false;

  // deconstruct all required props
  const {
    apiKey,
    apiSecret,
    deviceId,
    deviceLabel,
    deviceName,
    serverUri,
  } = value;

  // check type of required
  if (typeof apiKey !== 'string') return false;
  if (typeof apiSecret !== 'string') return false;
  if (typeof deviceId !== 'string') return false;
  if (typeof deviceLabel !== 'string') return false;
  if (typeof deviceName !== 'string') return false;
  if (typeof serverUri !== 'string') return false;

  // the following unused declarations trigger an error if the interface changed but the guard is not up to date. DO NOT REMOVE
  const obj = { apiKey, apiSecret, deviceId, deviceLabel, deviceName, serverUri };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isValid: AppSetting = obj;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const noNewOptionalProps: Omit<Required<AppSetting>, keyof typeof obj> = {};

  return true;
}

export function isAccountingPartner(value: unknown): value is AccountingPartner {
  if (!isPlainObject(value)) return false;

  // Required fields
  if (!value.hasOwnProperty('id')) return false;
  if (!value.hasOwnProperty('company')) return false;

  // deconstruct all required props
  const {
    id,
    company,
  } = value;

  // check type of required
  if (typeof id !== 'number') return false;
  if (typeof company !== 'string') return false;

  // the following unused declarations trigger an error if the interface changed but the guard is not up to date. DO NOT REMOVE
  const obj = { id, company };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isValid: AccountingPartner = obj;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const noNewOptionalProps: Omit<Required<AccountingPartner>, keyof typeof obj> = {};

  return true;
}

export function isService(value: unknown): value is Service {
  if (!isPlainObject(value)) return false;

  // Required fields
  if (!value.hasOwnProperty('id')) return false;
  if (!value.hasOwnProperty('descriptionDe')) return false;
  if (!value.hasOwnProperty('descriptionEn')) return false;

  // deconstruct all required props
  const {
    id,
    descriptionDe,
    descriptionEn,
  } = value;

  // check type of required
  if (typeof id !== 'number') return false;
  if (typeof descriptionDe !== 'string') return false;
  if (typeof descriptionEn !== 'string') return false;

  // the following unused declarations trigger an error if the interface changed but the guard is not up to date. DO NOT REMOVE
  const obj = { id, descriptionDe, descriptionEn };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isValid: Service = obj;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const noNewOptionalProps: Omit<Required<Service>, keyof typeof obj> = {};

  return true;
}

export function isCustomer(value: unknown): value is Customer {
  if (!isPlainObject(value)) return false;

  // required fields
  if (!value.hasOwnProperty('id')) return false;
  if (!value.hasOwnProperty('contactEmployee')) return false;
  if (!value.hasOwnProperty('contactAddress')) return false;
  if (!value.hasOwnProperty('businessAddress')) return false;
  if (!value.hasOwnProperty('isWebsitePartner')) return false;
  if (!value.hasOwnProperty('useVoucher')) return false;
  if (!value.hasOwnProperty('useEcTerminal')) return false;
  if (!value.hasOwnProperty('isInternetAvailable')) return false;
  if (!value.hasOwnProperty('isSparkassenKunde')) return false;
  if (!value.hasOwnProperty('isEmployer')) return false;
  if (!value.hasOwnProperty('bankTransferDetailsId')) return false;
  if (!value.hasOwnProperty('bankTransferDetails')) return false;

  // deconstruct all required props
  const {
    id,
    contactEmployee,
    contactAddress,
    businessAddress,
    isWebsitePartner,
    useVoucher,
    useEcTerminal,
    isInternetAvailable,
    isSparkassenKunde,
    isEmployer,
    bankTransferDetailsId,
    bankTransferDetails,
  } = value;

  // check type of required
  if (typeof id !== 'number') return false;
  if (!isPerson(contactEmployee)) return false;
  if (!isAddress(contactAddress)) return false;
  if (!isAddress(businessAddress)) return false;
  if (typeof isWebsitePartner !== 'boolean') return false;
  if (typeof useVoucher !== 'boolean') return false;
  if (typeof useEcTerminal !== 'boolean') return false;
  if (typeof isInternetAvailable !== 'boolean') return false;
  if (typeof isSparkassenKunde !== 'boolean') return false;
  if (typeof isEmployer !== 'boolean') return false;
  if (typeof bankTransferDetailsId !== 'number') return false;
  if (!isBankTransferDetails(bankTransferDetails)) return false;

  // check optionals
  let companyName: string | undefined | null;
  let contactEmployeeId: number | undefined | null;
  let taxNumber: string | undefined | null;
  let vatIdentificationNumber: string | undefined | null;
  let contactAddressId: number | undefined | null;
  let businessAddressId: number | undefined | null;
  let pointOfSalesCount: number | undefined | null;
  let status: CustomerState | undefined | null;
  let interestedNumber: number | undefined | null;
  let provision: number | undefined | null;
  let businessBankAccountId: number | undefined | null;
  let businessBankAccount: BankAccount | undefined | null;
  let creditorId: number | undefined | null;
  let accountingPartnerId: number | undefined | null;
  let websiteUrl: string | undefined | null;
  let websiteLogo: Logo | undefined | null;
  let voucherAmount: number | undefined | null;
  let hardware: string | undefined | null;
  let deploymentDate: string | undefined | null;
  let category: string | undefined | null;
  let serviceIds: number[] | undefined | null;
  let bankAccountCreditorId: string | undefined | null;
  let contractStart: string | undefined | null;
  let contractEnd: string | undefined | null;

  if (value.hasOwnProperty('companyName')) {
    if (typeof value.companyName !== 'string' && value.companyName !== undefined && value.companyName  !== null) return false;
    companyName = value.companyName;
  }

  if (value.hasOwnProperty('contactEmployeeId')) {
    if (typeof value.contactEmployeeId !== 'number' && value.contactEmployeeId !== undefined && value.contactEmployeeId !== null) return false;
    contactEmployeeId = value.contactEmployeeId;
  }

  if (value.hasOwnProperty('taxNumber')) {
    if (typeof value.taxNumber !== 'string' && value.taxNumber !== undefined && value.taxNumber !== null) return false;
    taxNumber = value.taxNumber;
  }

  if (value.hasOwnProperty('vatIdentificationNumber')) {
    if (typeof value.vatIdentificationNumber !== 'string' && value.vatIdentificationNumber !== undefined && value.vatIdentificationNumber !== null) return false;
    vatIdentificationNumber = value.vatIdentificationNumber;
  }

  if (value.hasOwnProperty('contactAddressId')) {
    if (typeof value.contactAddressId !== 'number' && value.contactAddressId !== undefined && value.contactAddressId !== null) return false;
    contactAddressId = value.contactAddressId;
  }

  if (value.hasOwnProperty('businessAddressId')) {
    if (typeof value.businessAddressId !== 'number' && value.businessAddressId !== undefined && value.businessAddressId !== null) return false;
    businessAddressId = value.businessAddressId;
  }

  if (value.hasOwnProperty('pointOfSalesCount')) {
    if (typeof value.pointOfSalesCount !== 'number' && value.pointOfSalesCount !== undefined && value.pointOfSalesCount !== null) return false;
    pointOfSalesCount = value.pointOfSalesCount;
  }

  if (value.hasOwnProperty('status')) {
    if (typeof value.status !== 'string' && value.status !== undefined && value.status !== null) return false;
    status = value.status as CustomerState;
  }

  if (value.hasOwnProperty('interestedNumber')) {
    if (typeof value.interestedNumber !== 'number' && value.interestedNumber !== undefined && value.interestedNumber !== null) return false;
    interestedNumber = value.interestedNumber;
  }

  if (value.hasOwnProperty('provision')) {
    if (typeof value.provision !== 'number' && value.provision !== undefined && value.provision !== null) return false;
    provision = value.provision;
  }

  if (value.hasOwnProperty('businessBankAccountId')) {
    if (typeof value.businessBankAccountId !== 'number' && value.businessBankAccountId !== undefined && value.businessBankAccountId !== null) return false;
    businessBankAccountId = value.businessBankAccountId;
  }

  if (value.hasOwnProperty('businessBankAccount')) {
    if (!isBankAccount(value.businessBankAccount) && value.businessBankAccount !== undefined && value.businessBankAccount !== null) return false;
    businessBankAccount = value.businessBankAccount;
  }

  if (value.hasOwnProperty('creditorId')) {
    if (typeof value.creditorId !== 'number' && value.creditorId !== undefined && value.creditorId !== null) return false;
    creditorId = value.creditorId;
  }

  if (value.hasOwnProperty('accountingPartnerId')) {
    if (typeof value.accountingPartnerId !== 'number' && value.accountingPartnerId !== undefined && value.accountingPartnerId !== null) return false;
    accountingPartnerId = value.accountingPartnerId;
  }

  if (value.hasOwnProperty('websiteUrl')) {
    if (typeof value.websiteUrl !== 'string' && value.websiteUrl !== undefined && value.websiteUrl !== null) return false;
    websiteUrl = value.websiteUrl;
  }

  if (value.hasOwnProperty('websiteLogo')) {
    if (!isLogo(value.websiteLogo) && value.websiteLogo !== undefined && value.websiteLogo !== null) return false;
    websiteLogo = value.websiteLogo;
  }

  if (value.hasOwnProperty('voucherAmount')) {
    if (typeof value.voucherAmount !== 'number' && value.voucherAmount !== undefined && value.voucherAmount !== null) return false;
    voucherAmount = value.voucherAmount;
  }

  if (value.hasOwnProperty('hardware')) {
    if (typeof value.hardware !== 'string' && value.hardware !== undefined && value.hardware !== null) return false;
    hardware = value.hardware;
  }

  if (value.hasOwnProperty('deploymentDate')) {
    if (typeof value.deploymentDate !== 'string' && value.deploymentDate !== undefined && value.deploymentDate !== null) return false;
    deploymentDate = value.deploymentDate;
  }

  if (value.hasOwnProperty('category')) {
    if (typeof value.category !== 'string' && value.category !== undefined && value.category !== null) return false;
    category = value.category;
  }

  if (value.hasOwnProperty('serviceIds')) {
    if (!Array.isArray(value.serviceIds) && value.serviceIds !== undefined && value.serviceIds !== null) return false;
    if (value.serviceIds !== undefined && value.serviceIds != null && !value.serviceIds.every((id) => typeof id === 'number')) return false;
    serviceIds = value.serviceIds;
  }

  if (value.hasOwnProperty('bankAccountCreditorId')) {
    if (typeof value.bankAccountCreditorId !== 'string' && value.bankAccountCreditorId !== undefined && value.bankAccountCreditorId !== null) return false;
    bankAccountCreditorId = value.bankAccountCreditorId;
  }

  if (value.hasOwnProperty('contractStart')) {
    if (typeof value.contractStart !== 'string' && value.contractStart !== undefined && value.contractStart !== null) return false;
    contractStart = value.contractStart;
  }

  if (value.hasOwnProperty('contractEnd')) {
    if (typeof value.contractEnd !== 'string' && value.contractEnd !== undefined && value.contractEnd !== null) return false;
    contractEnd = value.contractEnd;
  }

  // the following unused declarations trigger an error if the interface changed but the guard is not up to date. DO NOT REMOVE
  const obj = {
    id,
    companyName,
    contactEmployee,
    contactAddress,
    businessAddress,
    isWebsitePartner,
    websiteLogo,
    useVoucher,
    useEcTerminal,
    isInternetAvailable,
    isSparkassenKunde,
    isEmployer,
    bankTransferDetailsId,
    bankTransferDetails,
    contactEmployeeId,
    taxNumber,
    vatIdentificationNumber,
    contactAddressId,
    businessAddressId,
    pointOfSalesCount,
    status,
    interestedNumber,
    provision,
    businessBankAccountId,
    businessBankAccount,
    creditorId,
    accountingPartnerId,
    websiteUrl,
    voucherAmount,
    hardware,
    deploymentDate,
    category,
    serviceIds,
    bankAccountCreditorId,
    contractStart,
    contractEnd,
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isValid: Customer = obj;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const noNewOptionalProps: Omit<Required<Customer>, keyof typeof obj> = {};

  return true;
}
